/**
 * Helper utilities and types for ActionEntityPreview.
 *
 * Extracted here to keep ActionEntityPreview.tsx within the project's
 * ~300-line file-size guideline while making these pure functions and
 * the component's props type independently importable and testable.
 */

import type { EnrichedActionData } from '../../lib/proposals/core/types';
import type { ActionVariant } from './shared/variants';

export interface ActionEntityPreviewProps {
  /**
   * CRM entity UUID. Create-path callers now always pass the id; the store
   * hook (`useCrmObjectSnapshot`) suppresses the fetch itself for `create`
   * overlays. `undefined` disables the slideout click (CrmClickableContent
   * renders unwrapped) and is now reserved for genuinely id-less callers.
   */
  entityId: string | undefined;
  /** CRM entity type (e.g. 'ahoy_contact') — determines which content component is used */
  entityType: string;
  /** Fallback display label when enrichedData/store don't supply a richer value */
  label: string;
  /** Enriched action data from the backend — powers name/label fallbacks in ContentComponent prop builders */
  enrichedData: EnrichedActionData | undefined;
  /** Org ID — required for the CRM store subscription */
  orgId: number | undefined;
  /** Optional EntityCard variant (only used when falling back to EntityCard for unknown entity types) */
  variant?: ActionVariant;
  /**
   * Render the compact chip variant of the ContentComponent (avatar + name
   * + entity-type pill) instead of the full card. Used by Link/Merge action
   * cards where two entities sit side-by-side and the full layout would
   * crowd the row. The slideout click behavior is unchanged.
   *
   * Compact rendering doesn't use flatProps, so the CRM store subscription
   * is skipped when `compact` is true — UNLESS a `CrmPreviewProvider` has a
   * preview overlay for this entity id (e.g. a checked sibling create
   * action projecting this entity's proposed name). In that case the
   * subscription runs so the chip reflects the live-projected value instead
   * of only the static backend-enriched label. Ordinary homogeneous compact
   * previews with no overlay (`MixedActionTable`, other list/table renders)
   * are unaffected — they still skip the subscription entirely.
   */
  compact?: boolean;
  /**
   * How the full card renders its associated records. `card` (default)
   * nests them as bordered sub-cards — the deal's company card with
   * industry / headcount / location. `chip` folds them into the subtitle
   * line as an inline badge, for cards where the record is context and
   * something else (a stage transition) is the hero.
   */
  nestedAssociations?: 'card' | 'chip';
  /**
   * Fallback flat properties to use when the CRM store doesn't have the
   * entity (e.g. for update actions whose entity isn't in CRM yet). When
   * the store provides data it takes priority; these fill in the gaps.
   */
  fallbackFlatProps?: Record<string, unknown>;
  /**
   * Force the de-emphasised "excluded" treatment (greyed, titled, announced
   * to screen readers) regardless of the preview overlay — e.g. an
   * association ACTION that is unchecked while its target's create is still
   * checked. Applies even outside a `CrmPreviewProvider`. Pass `false` (not
   * `undefined`) to keep the wrapper mounted so a later flip to `true`
   * de-emphasises in place rather than remounting the content.
   */
  excluded?: boolean;
  /**
   * Tooltip / screen-reader wording used when `excluded` is true. Falls
   * back to the overlay wording ("Won't be created – … is unchecked").
   */
  excludedMessage?: string;
  /**
   * Omit the compact chip's entity-type pill. Use when the surrounding
   * context already names the type (e.g. a relationship label above the
   * chip). Only affects `compact` rendering.
   */
  hideTypeBadge?: boolean;
}

/**
 * Derive the flatProps slot for a resolved association target.
 *
 * Content-component prop builders read slots like `flatProps.ahoy_company_name`
 * to populate their "Works at" / company sub-card rows. We inject resolved
 * association targets under the convention `<target_entity_type>_name` so
 * the prop builder can read the slot it cares about without anyone hard-
 * coding a per-source-entity-type mapping (e.g., no special handling for
 * contacts vs. deals — both paths through `ahoy_company` land in
 * `ahoy_company_name`). Unused injections (e.g. stage targets) are harmless
 * because no builder reads `ahoy_deal_pipeline_stage_name`.
 */
export function flatPropKeyForTargetType(targetTypeName: string): string {
  return `${targetTypeName}_name`;
}

/**
 * Derive the target entity_type_name from an association_type_name,
 * assuming the standard `<source>_to_<target>` naming convention used
 * across the CRM schema (e.g. `ahoy_deal_to_company` → `ahoy_company`,
 * `ahoy_contact_to_owner` → `ahoy_owner`).
 *
 * Custom association names that omit the `ahoy_` source prefix — e.g.
 * `custom_deal_to_company` — still target core CRM entity types, which
 * are always `ahoy_`-scoped. Always prefix the target base with `ahoy_`
 * so the hydration filter (HYDRATED_TARGET_TYPES) matches both standard
 * and custom associations that point at the same core target.
 *
 * Stage associations are excluded — they don't round-trip through the
 * normal fetch path, so treating them as "pending" forever would leave
 * any sub-card that consults pendingTargetTypes stuck in a skeleton.
 */
export function deriveTargetTypeFromAssociation(
  associationTypeName: string
): string | null {
  if (associationTypeName.endsWith('_stage')) {
    return null;
  }
  const match = associationTypeName.match(/^(.+)_to_(.+)$/);
  if (!match) {
    return null;
  }
  const [, , targetBase] = match;
  // Target base is already `ahoy_`-prefixed when the association name
  // carries it through (rare); otherwise synthesize the standard core
  // entity type name by prefixing with `ahoy_`.
  return targetBase.startsWith('ahoy_') ? targetBase : `ahoy_${targetBase}`;
}

/**
 * Target entity types whose resolved CrmObjects the current card builders
 * actually consume — as `flatProps.<type>_name` or `resolvedTargets[<type>]`.
 * Fetching beyond this set is wasted work: rendering one proposal row
 * would otherwise kick off a background fetch for every peripheral
 * association on the source entity (owners, deals, risks, …) even though
 * no card reads them.
 *
 * Widen this set only alongside a builder that genuinely needs the new
 * target type.
 */
export const HYDRATED_TARGET_TYPES = new Set<string>(['ahoy_company']);
